import { useState } from 'react';
import type { CostResolutionPreviewDto } from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { scopeLevelLabel } from './shared';
import {
  useComponentTypes,
  useConfirmCostResolution,
  useCostCatalogs,
  usePreviewCostResolution,
  useReferentialMeasurementUnits,
} from './hooks';

/**
 * « Coûts » — résolution tarifaire d'un dossier (DEC-005). L'expert prévisualise le coût applicable ;
 * un repli (FALLBACK) est TOUJOURS visible et exige une **confirmation explicite** (case à cocher) —
 * jamais silencieux — avant de pouvoir confirmer. Le serveur reste l'autorité (il renvoie 409 si le
 * repli n'est pas confirmé) et recalcule à la confirmation, produisant un snapshot immuable haché.
 */
export function CostResolutionTab({ dossierId }: { dossierId: string }): JSX.Element {
  const catalogs = useCostCatalogs();
  const componentTypes = useComponentTypes();
  const units = useReferentialMeasurementUnits();
  const preview = usePreviewCostResolution(dossierId);
  const confirm = useConfirmCostResolution(dossierId);

  const [valuationDate, setValuationDate] = useState('');
  const [catalogCode, setCatalogCode] = useState('');
  const [componentTypeId, setComponentTypeId] = useState('');
  const [measurementUnitId, setMeasurementUnitId] = useState('');
  const [result, setResult] = useState<CostResolutionPreviewDto | null>(null);
  const [fallbackConfirmed, setFallbackConfirmed] = useState(false);
  const [overrideCost, setOverrideCost] = useState('');
  const [justification, setJustification] = useState('');

  const canPreview =
    valuationDate !== '' &&
    catalogCode !== '' &&
    componentTypeId !== '' &&
    measurementUnitId !== '';
  const overrideEntered = overrideCost.trim() !== '';
  const overrideNeedsJustification = overrideEntered && justification.trim() === '';
  const fallbackBlocks = result?.mode === 'FALLBACK' && !fallbackConfirmed;
  const canConfirm =
    result !== null &&
    result.mode !== 'BLOCKED' &&
    !fallbackBlocks &&
    !overrideNeedsJustification &&
    !confirm.isPending;

  const runPreview = (): void => {
    if (!canPreview) return;
    setFallbackConfirmed(false);
    preview.mutate(
      { valuationDate, catalogCode, componentTypeId, measurementUnitId },
      { onSuccess: (data) => setResult(data) },
    );
  };

  const runConfirm = (): void => {
    if (!result || !canConfirm) return;
    confirm.mutate({
      valuationDate,
      catalogCode,
      componentTypeId,
      measurementUnitId,
      ...(result.mode === 'FALLBACK' ? { confirmFallback: fallbackConfirmed } : {}),
      ...(overrideEntered
        ? { override: { overrideCost: overrideCost.trim(), justification: justification.trim() } }
        : {}),
    });
  };

  const modeBadge = (mode: string): JSX.Element => {
    if (mode === 'EXACT_MATCH') return <Badge tone="success">Correspondance exacte</Badge>;
    if (mode === 'FALLBACK') return <Badge tone="warning">Repli (fallback)</Badge>;
    return <Badge tone="danger">Bloqué</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Résolution tarifaire</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="cr-date">Date de valorisation</Label>
            <Input
              id="cr-date"
              type="date"
              value={valuationDate}
              onChange={(e) => setValuationDate(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="cr-cat">Catalogue</Label>
            <Select
              id="cr-cat"
              value={catalogCode}
              onChange={(e) => setCatalogCode(e.target.value)}
            >
              <option value="">— Choisir —</option>
              {(catalogs.data ?? []).map((c) => (
                <option key={c.id} value={c.code}>
                  {c.code} — {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="cr-type">Type de composant</Label>
            <Select
              id="cr-type"
              value={componentTypeId}
              onChange={(e) => setComponentTypeId(e.target.value)}
            >
              <option value="">— Choisir —</option>
              {(componentTypes.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} — {t.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="cr-unit">Unité</Label>
            <Select
              id="cr-unit"
              value={measurementUnitId}
              onChange={(e) => setMeasurementUnitId(e.target.value)}
            >
              <option value="">— Choisir —</option>
              {(units.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.code} — {u.name}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <Button type="button" onClick={runPreview} disabled={!canPreview || preview.isPending}>
          {preview.isPending ? 'Prévisualisation…' : 'Prévisualiser'}
        </Button>

        {preview.isError ? (
          <Alert variant="error" details={toDisplayError(preview.error).details}>
            {toDisplayError(preview.error).message}
          </Alert>
        ) : null}

        {result ? (
          <div className="space-y-3 rounded-md border border-slate-200 p-3">
            <div className="flex flex-wrap items-center gap-2">
              {modeBadge(result.mode)}
              {result.fallbackLevel ? (
                <span className="text-sm text-slate-600">
                  Niveau appliqué : {scopeLevelLabel(result.fallbackLevel)}
                </span>
              ) : null}
              {result.unitCost ? (
                <span className="ml-auto text-sm font-semibold">{result.unitCost.amount} FCFA</span>
              ) : null}
            </div>

            {result.mode === 'BLOCKED' ? (
              <Alert variant="error">
                Aucun coût applicable pour cette date/portée : la résolution est bloquée.
              </Alert>
            ) : null}

            {result.mode === 'FALLBACK' ? (
              <label className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-sm text-amber-900">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={fallbackConfirmed}
                  onChange={(e) => setFallbackConfirmed(e.target.checked)}
                />
                <span>
                  Je confirme l'utilisation du niveau de repli (fallback) —{' '}
                  {scopeLevelLabel(result.fallbackLevel ?? '')}. Le coût du niveau le plus précis
                  n'existe pas ; ce choix sera tracé et audité.
                </span>
              </label>
            ) : null}

            {result.mode !== 'BLOCKED' ? (
              <fieldset className="space-y-2 border-t border-slate-100 pt-3">
                <legend className="text-xs uppercase tracking-wide text-slate-400">
                  Ajustement manuel (facultatif)
                </legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="cr-ovr">Coût ajusté (FCFA)</Label>
                    <Input
                      id="cr-ovr"
                      inputMode="decimal"
                      value={overrideCost}
                      onChange={(e) => setOverrideCost(e.target.value)}
                      placeholder="laisser vide = coût du catalogue"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cr-just">
                      Justification {overrideEntered ? '(obligatoire)' : ''}
                    </Label>
                    <Input
                      id="cr-just"
                      value={justification}
                      onChange={(e) => setJustification(e.target.value)}
                    />
                  </div>
                </div>
                {overrideNeedsJustification ? (
                  <p className="text-xs text-rose-600">
                    Une justification est obligatoire pour un ajustement manuel.
                  </p>
                ) : null}
              </fieldset>
            ) : null}

            {result.mode !== 'BLOCKED' ? (
              <Button type="button" onClick={runConfirm} disabled={!canConfirm}>
                {confirm.isPending ? 'Confirmation…' : 'Confirmer'}
              </Button>
            ) : null}

            {confirm.isError ? (
              <Alert variant="error" details={toDisplayError(confirm.error).details}>
                {toDisplayError(confirm.error).message}
              </Alert>
            ) : null}

            {confirm.data ? (
              <Alert variant="success">
                Résolution confirmée ({confirm.data.resolutionMode}) — coût appliqué{' '}
                {confirm.data.appliedCost} FCFA. Empreinte :{' '}
                {confirm.data.snapshotHash.slice(0, 10)}…
              </Alert>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
